/**
 * Aplica o rascunho da conversa na tela hospedeira pelos aplicadores
 * registrados ou pelos controles visíveis do DOM. Recusa dados sensíveis
 * e campos já preenchidos; dispara os eventos de edição sem salvar a tela.
 * O resultado informa campos aplicados e recusados para a barra de revisão.
 */
import {
  JanelaObservavel,
  ehCampoSensivel,
  ehCampoVisivel,
  mapaDeRotulosPorFor,
  rotuloDoCampo,
  tipoDoCampo
} from './observadorDeTela'
import {
  casarCampoComControle
} from './casamentoDeCampo'
import {
  aplicarRascunhoComAplicador,
  obterAplicadorDeRascunhoDaTela
} from './aplicadorDeRascunhoDaTela'
import type {
  CampoObservadoParaCasamento,
  MotivoDoCasamento
} from './casamentoDeCampo'

/** Controles nativos em que a escrita é equivalente à digitação. */
const TIPOS_DE_TEXTO = new Set(['text', 'search', 'textarea'])

/** Campos nativos e papéis de componentes que podem ser encontrados para recusar por motivo. */
const SELETOR_DE_CONTROLES = 'input, select, textarea, [role="combobox"], [role="listbox"]'

export type MotivoDeRecusa =
  | MotivoDoCasamento
  | 'campo_ocupado'
  | 'campo_sensivel'
  | 'campo_nao_suportado'
  | 'opcao_nao_encontrada'
  | 'data_invalida'
  // Os dois abaixo só a escrita por APLICADOR produz, mas moram aqui porque o
  // vocabulário de recusa é um só: é este tipo que a barra de aceitar/limpar
  // traduz para a pessoa, e uma união paralela deixaria metade sem copy.
  | 'valor_vazio'
  | 'falha_ao_aplicar'
  // O valor pede um REGISTRO (a pessoa no seletor de Verificar folhas) e a
  // procura não achou ninguém, ou achou mais de um sem como escolher.
  | 'registro_nao_encontrado'
  | 'mais_de_um_registro'

export interface CampoPreenchido {
  campo: string
  valor: string
  /** O rótulo como a PESSOA o lê na tela — é o que a barra vai nomear. */
  rotulo: string
  /** Valor do controle imediatamente antes da escrita, para Limpar desfazer. */
  valor_anterior: string
  /** Valores selecionados anteriores de um `<select multiple>` nativo. */
  valores_selecionados_anteriores?: string[]
}

export interface CampoRecusado {
  campo: string
  valor: string
  motivo: MotivoDeRecusa
}

export interface ResultadoDoPreenchimento {
  aplicados: CampoPreenchido[]
  recusados: CampoRecusado[]
}

export interface JanelaPreenchivel extends JanelaObservavel {
  Event?: new (tipo: string, opcoes?: { bubbles?: boolean }) => Event
}

export type ConsumidorDeContextoDeAbertura = (
  identificadorTela: string,
  handle: string
) => Promise<Record<string, unknown> | null>

/**
 * Ponto de injeção do host, no mesmo padrão da colheita de contexto de tela: o
 * painel tem o `contentWindow`, mas não pode importar o cliente HTTP (o ciclo
 * `assistantClient -> @/main -> store -> state` existe e é real). Quem tem o
 * cliente — `state.ts` — registra o consumo; o painel só o aciona.
 */
let consumidorRegistrado: ConsumidorDeContextoDeAbertura | null = null

export function registrarConsumidorDeContextoDeAbertura (
  consumidor: ConsumidorDeContextoDeAbertura | null
): void {
  consumidorRegistrado = consumidor
}

/**
 * Consome o handle publicado no envelope. Sem consumidor registrado, handle
 * expirado ou 404: `null`, e a tela abre vazia — o comportamento de hoje.
 */
export async function consumirContextoDeAbertura (
  identificadorTela: string,
  handle: string
): Promise<Record<string, unknown> | null> {
  if (consumidorRegistrado === null) return null
  try {
    return await consumidorRegistrado(identificadorTela, handle)
  } catch {
    return null
  }
}

/** Idêntica à normalização do backend: minúsculas, sem acento, sem sobra. */
const MARCAS_DIACRITICAS = /\p{Diacritic}/gu

export function normalizarRotulo (rotulo: string): string {
  return rotulo
    .normalize('NFD')
    .replace(MARCAS_DIACRITICAS, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

interface ControleObservado extends CampoObservadoParaCasamento {
  elemento: Element
}

interface ResultadoDaLocalizacao {
  elemento: Element | null
  rotulo: string | null
  motivo: MotivoDoCasamento | null
}

function tipoDoElemento (elemento: Element): string {
  return elemento.tagName.toLowerCase() === 'input'
    ? (elemento.getAttribute('type') || 'text').toLowerCase()
    : tipoDoCampo(elemento)
}

function ehCampoMascarado (elemento: Element): boolean {
  const atributosDeMascara = [
    'data-mask',
    'data-mascara',
    'mask',
    'data-masked',
    'data-masked-input'
  ]
  if (atributosDeMascara.some(atributo => elemento.hasAttribute(atributo))) return true

  const classes = typeof elemento.className === 'string' ? elemento.className : ''
  return /mask(?:ed|ing)?|inputmask/i.test(classes)
}

function motivoDeSuporte (elemento: Element): 'campo_nao_suportado' | null {
  const tag = elemento.tagName.toLowerCase()
  const tipo = tipoDoElemento(elemento)
  if (tag === 'textarea') return null
  if (tag === 'select') return null
  if (tag !== 'input') return 'campo_nao_suportado'
  if (tipo === 'date') return null
  if (TIPOS_DE_TEXTO.has(tipo) && !ehCampoMascarado(elemento)) return null
  return 'campo_nao_suportado'
}

function estaVazio (elemento: Element): boolean {
  return String((elemento as HTMLInputElement).value ?? '').trim().length === 0
}

interface CampoLocalizado {
  elemento: Element
  rotulo: string
}

/**
 * Observa os controles visíveis, mantendo também os não suportados para que
 * uma correspondência válida possa virar uma recusa nomeada em vez de sumir.
 */
function controlesObservados (janela: JanelaPreenchivel, raiz?: Element | null): ControleObservado[] {
  let documento: Document | null | undefined
  try {
    documento = janela.document
  } catch {
    return []
  }
  if (!documento || typeof documento.querySelectorAll !== 'function') return []

  const rotulosPorFor = mapaDeRotulosPorFor(documento)
  return Array.from((raiz || documento).querySelectorAll(SELETOR_DE_CONTROLES))
    .filter(elemento => ehCampoVisivel(elemento, janela))
    .map(elemento => {
      const { rotulo } = rotuloDoCampo(elemento, documento, rotulosPorFor)
      return {
        elemento,
        nome: elemento.getAttribute('name'),
        id: elemento.getAttribute('id'),
        rotulo
      }
    })
}

function localizarCampoComMotivo (
  janela: JanelaPreenchivel,
  campo: string,
  raiz?: Element | null
): ResultadoDaLocalizacao {
  const casamento = casarCampoComControle(campo, controlesObservados(janela, raiz))
  if (casamento.controle === null) {
    return { elemento: null, rotulo: null, motivo: casamento.motivo }
  }

  return {
    elemento: casamento.controle.elemento,
    rotulo: casamento.controle.rotulo ?? null,
    motivo: null
  }
}

export function localizarCampo (
  janela: JanelaPreenchivel,
  campo: string,
  raiz?: Element | null
): CampoLocalizado | null {
  const localizado = localizarCampoComMotivo(janela, campo, raiz)
  if (localizado.elemento === null) return null
  return {
    elemento: localizado.elemento,
    rotulo: localizado.rotulo || campo
  }
}

function eventoDe (janela: JanelaPreenchivel, tipo: string): Event | null {
  const Construtor = janela.Event
  if (typeof Construtor !== 'function') return null
  return new Construtor(tipo, { bubbles: true })
}

/**
 * Escreve como quem digita: valor, `input`, `change`. É o `input` que faz o
 * `v-model` da tela de produto enxergar o texto; sem ele o campo mostraria
 * algo que o formulário não sabe que existe.
 */
/**
 * Escrever é digitar: mesmo valor, mesmos eventos nativos. Exportada porque o
 * aplicador de uma tela específica precisa da MESMA escrita — se ele setasse
 * `value` sem os eventos, `v-model`, máscara e watcher não rodariam, e o store
 * ficaria dizendo uma coisa com a tela mostrando outra.
 */
export function escreverComoDigitacao (
  janela: JanelaPreenchivel,
  elemento: Element,
  valor: string
): void {
  const alvo = elemento as HTMLInputElement
  alvo.value = valor
  for (const tipo of ['input', 'change']) {
    const evento = eventoDe(janela, tipo)
    if (evento) elemento.dispatchEvent(evento)
  }
}

interface EstadoAnterior {
  valor: string
  valoresSelecionados?: string[]
}

function estadoAnterior (elemento: Element): EstadoAnterior {
  if (elemento.tagName.toLowerCase() === 'select') {
    const select = elemento as HTMLSelectElement
    return {
      valor: String(select.value ?? ''),
      valoresSelecionados: Array.from(select.options)
        .filter(opcao => opcao.selected)
        .map(opcao => opcao.value)
    }
  }
  return { valor: String((elemento as HTMLInputElement).value ?? '') }
}

function opcaoEstaDesabilitada (opcao: HTMLOptionElement): boolean {
  const grupo = opcao.parentElement
  return opcao.disabled || (
    grupo?.tagName.toLowerCase() === 'optgroup' &&
    (grupo as HTMLOptGroupElement).disabled
  )
}

function encontrarOpcaoPorTexto (
  select: HTMLSelectElement,
  valor: string
): HTMLOptionElement | null {
  const procurado = normalizarRotulo(valor)
  const opcoes = Array.from(select.options)
    .filter(opcao => !opcaoEstaDesabilitada(opcao))
    .map(opcao => ({ opcao, texto: normalizarRotulo(opcao.textContent || '') }))
    .filter(({ texto }) => texto.length > 0)

  const exata = opcoes.find(({ texto }) => texto === procurado)
  if (exata) return exata.opcao

  const prefixada = opcoes.find(({ texto }) => texto.startsWith(`${procurado} `))
  return prefixada?.opcao || null
}

function escreverSelecao (
  janela: JanelaPreenchivel,
  select: HTMLSelectElement,
  opcao: HTMLOptionElement
): void {
  if (select.multiple) {
    for (const candidata of Array.from(select.options)) candidata.selected = false
    opcao.selected = true
  } else {
    select.value = opcao.value
  }

  for (const tipo of ['input', 'change']) {
    const evento = eventoDe(janela, tipo)
    if (evento) select.dispatchEvent(evento)
  }
}

function dataEstaEmFormatoISO (valor: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(valor)) return false
  const [ano, mes, dia] = valor.split('-').map(Number)
  if (ano < 1 || mes < 1 || mes > 12 || dia < 1 || dia > 31) return false
  const data = new Date(Date.UTC(ano, mes - 1, dia))
  return data.getUTCFullYear() === ano &&
    data.getUTCMonth() === mes - 1 &&
    data.getUTCDate() === dia
}

function restaurarEstadoAnterior (
  janela: JanelaPreenchivel,
  elemento: Element,
  aplicado: CampoPreenchido
): void {
  if (elemento.tagName.toLowerCase() === 'select' && aplicado.valores_selecionados_anteriores) {
    const select = elemento as HTMLSelectElement
    const anteriores = new Set(aplicado.valores_selecionados_anteriores)
    for (const opcao of Array.from(select.options)) opcao.selected = anteriores.has(opcao.value)
    for (const tipo of ['input', 'change']) {
      const evento = eventoDe(janela, tipo)
      if (evento) select.dispatchEvent(evento)
    }
    return
  }

  escreverComoDigitacao(janela, elemento, aplicado.valor_anterior ?? '')
}

/** Destaque do que veio da conversa — some quando a pessoa decide. */
export function destacar (elemento: Element): void {
  const estilo = (elemento as HTMLElement).style
  if (!estilo) return
  estilo.outline = '2px solid #2f6fed'
  estilo.outlineOffset = '2px'
}

export function removerDestaque (elemento: Element): void {
  const estilo = (elemento as HTMLElement).style
  if (!estilo) return
  estilo.outline = ''
  estilo.outlineOffset = ''
}

/**
 * Uma passada de preenchimento. Devolve o que entrou e o que foi recusado, com
 * o motivo — recusa silenciosa aqui viraria "ele entendeu?" de novo, que é
 * exatamente o custo que o C16 mediu.
 */
export function aplicarRascunhoNaTela (
  janela: JanelaPreenchivel | null | undefined,
  rascunho: Record<string, unknown> | null | undefined,
  raiz?: Element | null
): ResultadoDoPreenchimento {
  const resultado: ResultadoDoPreenchimento = { aplicados: [], recusados: [] }
  if (!janela || !rascunho) return resultado

  for (const [campo, bruto] of Object.entries(rascunho)) {
    if (typeof bruto !== 'string' || bruto.trim().length === 0) continue
    const valor = bruto.trim()

    const localizado = localizarCampoComMotivo(janela, campo, raiz)
    if (localizado.elemento === null) {
      resultado.recusados.push({
        campo,
        valor,
        motivo: localizado.motivo || 'campo_nao_encontrado'
      })
      continue
    }
    const elemento = localizado.elemento
    const rotulo = localizado.rotulo || campo

    // A régua de privacidade não se move: o que o observador não LÊ, este
    // módulo não ESCREVE.
    if (ehCampoSensivel({
      tipo: tipoDoCampo(elemento),
      nome: elemento.getAttribute('name'),
      id: elemento.getAttribute('id'),
      rotulo,
      placeholder: elemento.getAttribute('placeholder'),
      autocomplete: elemento.getAttribute('autocomplete')
    })) {
      resultado.recusados.push({ campo, valor, motivo: 'campo_sensivel' })
      continue
    }

    const motivoDeTipo = motivoDeSuporte(elemento)
    if (motivoDeTipo) {
      resultado.recusados.push({ campo, valor, motivo: motivoDeTipo })
      continue
    }

    if (!estaVazio(elemento)) {
      resultado.recusados.push({ campo, valor, motivo: 'campo_ocupado' })
      continue
    }

    const anterior = estadoAnterior(elemento)
    if (elemento.tagName.toLowerCase() === 'select') {
      const opcao = encontrarOpcaoPorTexto(elemento as HTMLSelectElement, valor)
      if (opcao === null) {
        resultado.recusados.push({ campo, valor, motivo: 'opcao_nao_encontrada' })
        continue
      }
      escreverSelecao(janela, elemento as HTMLSelectElement, opcao)
    } else if (tipoDoElemento(elemento) === 'date') {
      if (!dataEstaEmFormatoISO(valor)) {
        resultado.recusados.push({ campo, valor, motivo: 'data_invalida' })
        continue
      }
      escreverComoDigitacao(janela, elemento, valor)
    } else {
      escreverComoDigitacao(janela, elemento, valor)
    }
    destacar(elemento)
    resultado.aplicados.push({
      campo,
      valor,
      rotulo,
      valor_anterior: anterior.valor,
      ...(anterior.valoresSelecionados
        ? { valores_selecionados_anteriores: anterior.valoresSelecionados }
        : {})
    })
  }
  return resultado
}

/** Desfaz o preenchimento com os MESMOS eventos: limpar também é digitar. */
export function limparPreenchimento (
  janela: JanelaPreenchivel | null | undefined,
  aplicados: readonly CampoPreenchido[],
  raiz?: Element | null
): void {
  if (!janela) return
  for (const aplicado of aplicados) {
    const localizado = localizarCampo(janela, aplicado.campo, raiz)
    if (!localizado) continue
    restaurarEstadoAnterior(janela, localizado.elemento, aplicado)
    removerDestaque(localizado.elemento)
  }
}

/** Só tira o destaque — o valor fica, que é o que "Manter" quer dizer. */
export function manterPreenchimento (
  janela: JanelaPreenchivel | null | undefined,
  aplicados: readonly CampoPreenchido[],
  raiz?: Element | null
): void {
  if (!janela) return
  for (const aplicado of aplicados) {
    const localizado = localizarCampo(janela, aplicado.campo, raiz)
    if (localizado) removerDestaque(localizado.elemento)
  }
}

export const INTERVALO_DE_TENTATIVA_MS = 250
export const TETO_DE_ESPERA_MS = 8000

/**
 * Aguarda os controles da rota renderizarem, com limite de tempo e
 * cancelamento ao trocar o contexto da abertura.
 */
export async function aplicarQuandoRenderizar (
  obterJanela: () => JanelaPreenchivel | null | undefined,
  rascunho: Record<string, unknown> | null | undefined,
  opcoes: {
    esperar?: (ms: number) => Promise<void>
    agora?: () => number
    tetoMs?: number
    ativo?: () => boolean
    raiz?: Element | null
  } = {}
): Promise<ResultadoDoPreenchimento> {
  const esperar = opcoes.esperar || (ms => new Promise<void>(pronto => setTimeout(pronto, ms)))
  const agora = opcoes.agora || (() => Date.now())
  const teto = opcoes.tetoMs ?? TETO_DE_ESPERA_MS
  const inicio = agora()

  let ultimo: ResultadoDoPreenchimento = { aplicados: [], recusados: [] }
  for (;;) {
    if (opcoes.ativo && !opcoes.ativo()) return { aplicados: [], recusados: [] }
    // A TELA tem preferência sobre o raspador genérico, e não é cortesia: onde
    // o valor editável não está no DOM — a célula da folha vive no `data()` do
    // componente — o caminho genérico não tem o que casar. Mesma inversão que
    // `provedorDeContextoDaTela` já faz na leitura, e pelo mesmo motivo.
    const janela = obterJanela()
    // Consulta os aplicadores registrados pela tela hospedeira.
    const daTela = obterAplicadorDeRascunhoDaTela(janela)
    ultimo = daTela === null
      ? aplicarRascunhoNaTela(janela, rascunho, opcoes.raiz)
      : await aplicarRascunhoComAplicador(daTela, rascunho, opcoes.ativo)
    if (opcoes.ativo && !opcoes.ativo()) return { aplicados: [], recusados: [] }
    // O aplicador da tela responde só pelo que ELE endereça, e o registro tem
    // uma vaga só: uma tabela montada num canto da rota registra a tradução
    // dela e passa a ver todo rascunho que chega. Quando ele não reconhece
    // NADA, o caminho genérico ainda pode reconhecer — e não tentar era o pior
    // desfecho possível: nem preenchia, nem dizia que não preencheu. Medido em
    // 21/09/2026, na ficha cadastral do empregado.
    if (daTela !== null && ultimo.aplicados.length === 0 && ultimo.recusados.length === 0) {
      ultimo = aplicarRascunhoNaTela(janela, rascunho, opcoes.raiz)
    }
    if (ultimo.aplicados.length > 0) return await confirmarQueFicou(janela, ultimo, esperar, opcoes.raiz)
    // Recusa DECIDIDA (campo sensível, campo já ocupado) não se resolve
    // esperando: o poll é só para o campo que ainda não renderizou.
    if (ultimo.recusados.every(recusado => recusado.motivo !== 'campo_nao_encontrado')) {
      return ultimo
    }
    if (agora() - inicio >= teto) return ultimo
    await esperar(INTERVALO_DE_TENTATIVA_MS)
  }
}

/** Quanto se espera antes de reler o campo escrito. */
export const ESPERA_PARA_CONFIRMAR_MS = 700

/**
 * O valor escrito continua lá depois que a tela terminou de carregar?
 *
 * Numa tela de EDIÇÃO o campo nasce vazio e recebe o registro quando a
 * requisição volta. Escrever nesse intervalo passa por todas as guardas — o
 * campo está mesmo vazio — e mesmo assim o valor some, sobrescrito pelo dado
 * do servidor. Medido em 21/09/2026 na ficha cadastral: a barra dizia
 * "Preenchi nome com 'Ana Pontos MCP Silva'" e a tela mostrava o nome antigo.
 *
 * Reler e rebaixar é a única saída honesta: `campo_ocupado` é exatamente o que
 * aconteceu — havia conteúdo, ele só chegou depois.
 */
async function confirmarQueFicou (
  janela: JanelaPreenchivel | null | undefined,
  resultado: ResultadoDoPreenchimento,
  esperar: (ms: number) => Promise<void>,
  raiz?: Element | null
): Promise<ResultadoDoPreenchimento> {
  await esperar(ESPERA_PARA_CONFIRMAR_MS)

  const aplicados: CampoPreenchido[] = []
  const recusados: CampoRecusado[] = [...resultado.recusados]

  if (janela === null || janela === undefined) return resultado

  for (const aplicado of resultado.aplicados) {
    const agora = localizarCampo(janela, aplicado.campo, raiz)
    // Sem conseguir reler, mantém o que foi dito: inventar recusa aqui seria
    // trocar um relato impreciso por outro.
    const valorAtual = agora === null ? null : valorDeControle(agora.elemento)
    if (valorAtual !== null && !mesmoConteudo(valorAtual, aplicado.valor)) {
      recusados.push({ campo: aplicado.campo, valor: aplicado.valor, motivo: 'campo_ocupado' })
      continue
    }
    aplicados.push(aplicado)
  }

  return { aplicados, recusados }
}

/**
 * Dois valores dizem a mesma coisa, ignorando pontuacao de mascara?
 *
 * `1133334444` entra num campo mascarado e vira `(11)3333-4444`. Comparar
 * string crua via diferenca onde nao ha: o conteudo continua la, so foi
 * formatado pela tela. Medido em 21/09/2026, no telefone do local de trabalho
 * — a barra dizia "nao preenchi" com o valor visivel no campo.
 *
 * O que muda de verdade — a carga da tela devolvendo o registro antigo — segue
 * sendo diferenca: "Ana Pontos MCP" e "Ana Pontos MCP Silva" nao se confundem.
 */
function mesmoConteudo (um: string, outro: string): boolean {
  const so = (valor: string) => valor.replace(/[^\p{L}\p{N}]+/gu, '').toLowerCase()
  return so(um) === so(outro)
}

function valorDeControle (elemento: Element): string | null {
  const valor = (elemento as { value?: unknown }).value
  return typeof valor === 'string' ? valor : null
}

/**
 * O texto da barra. Duas leituras possíveis, e nenhuma delas é o silêncio:
 * preenchi (e a pessoa aceita ou limpa), ou não preenchi (e a pessoa lê o que
 * disse, para copiar). O item já saiu da frase verbal do turno — se a barra
 * calasse aqui, o dado sumiria de vez.
 */
export function textoDoPreenchimento (
  resultado: ResultadoDoPreenchimento,
  traduzir: (chave: string, parametros?: Record<string, unknown>) => string
): { tom: 'aplicado' | 'nao_aplicado', texto: string } | null {
  if (resultado.aplicados.length > 0) {
    const campos = resultado.aplicados
      .map(aplicado => traduzir('assistentePontotel.tela.preenchimento.par', {
        rotulo: aplicado.rotulo,
        valor: aplicado.valor
      }))
      .join('; ')
    const aplicado = traduzir('assistentePontotel.tela.preenchimento.aplicado', { campos })
    // Um turno pode preencher DOIS campos e recusar outros dois. Enquanto só o
    // sucesso aparecia, a pessoa lia "preenchi nome e código" numa tela em que
    // a carga horária tinha sido recusada — e ficava sem saber, porque o que
    // faltou não era dito em lugar nenhum. Medido em 21/09/2026, na criação de
    // escala.
    const recusa = textoDaRecusa(resultado, traduzir)
    return {
      tom: 'aplicado',
      texto: recusa === null ? aplicado : `${aplicado} ${recusa}`
    }
  }
  if (resultado.recusados.length > 0) {
    const campos = resultado.recusados
      .map(recusado => {
        const detalhe = traduzir(`assistentePontotel.tela.preenchimento.recusa.${recusado.motivo}`, {
          campo: recusado.campo,
          valor: recusado.valor
        })
        return `${recusado.campo}: ${recusado.valor} (${detalhe})`
      })
      .join('; ')
    return {
      tom: 'nao_aplicado',
      texto: traduzir('assistentePontotel.tela.preenchimento.naoAplicado', { campos })
    }
  }
  return null
}

/** A parte "e o que NAO entrou" da barra, ou `null` se tudo entrou. */
function textoDaRecusa (
  resultado: ResultadoDoPreenchimento,
  traduzir: (chave: string, parametros?: Record<string, unknown>) => string
): string | null {
  if (resultado.recusados.length === 0) return null
  const campos = resultado.recusados
    .map(recusado => {
      const detalhe = traduzir(`assistentePontotel.tela.preenchimento.recusa.${recusado.motivo}`, {
        campo: recusado.campo,
        valor: recusado.valor
      })
      return `${recusado.campo}: ${recusado.valor} (${detalhe})`
    })
    .join('; ')
  return traduzir('assistentePontotel.tela.preenchimento.naoAplicado', { campos })
}
